import {ExclamationCircleOutlined} from '@ant-design/icons';
import {OperationTypes} from '@jimmyn/apollo-client';
import {Col, Modal, Row} from 'antd';
import {
  MetadataItemInput,
  PaymentMethods,
  ProviderFieldsFragment,
  useAccountQuery,
  useCreateProviderMutation,
  useProviderQuery,
  useUpdateProviderMutation
} from 'api/generated';
import {providersQuery} from 'api/queries';
import {Ellipsize} from 'components/Ellipsize';
import {PageHeader} from 'components/PageHeader';
import {Stack} from 'components/Stack';
import {acquirerLabels, paymentMethodLabels} from 'lib/labels';
import {fromCents, nullify, toCents} from 'lib/utils';
import React from 'react';
import {RouteComponentProps, useHistory} from 'react-router-dom';
import {Acquirer} from 'types/api';
import {EditProviderForm, FormValues, metaToObject} from './EditProviderForm';
import {ProviderStatusTags} from './ProviderStatusTag';

type Props = {
  providerId: string;
  accountId: string;
};

export const EditProvider: React.FC<RouteComponentProps<Props>> = ({match}) => {
  const {providerId, accountId} = match.params;
  const isNew = providerId === undefined;

  const {data: {provider} = {}, loading} = useProviderQuery({
    skip: isNew,
    variables: {id: providerId}
  });

  const {data: accountData, loading: loadingAccount} = useAccountQuery({
    variables: {id: accountId}
  });
  const account = accountData?.account;

  const title = provider
    ? `${acquirerLabels[provider?.acquirer as Acquirer]} - ${
        paymentMethodLabels[provider?.paymentMethod as PaymentMethods]
      }`
    : account?.name || account?.business?.companyName || account?.business?.legalName || '';

  const history = useHistory();
  const [updateProvider] = useUpdateProviderMutation();
  const [createProvider] = useCreateProviderMutation({
    updateQuery: {query: providersQuery, variables: {accountId}},
    operationType: OperationTypes.ADD
  });

  const initialValues: FormValues = {
    accountId,
    processor: provider?.processor,
    enabled: provider?.enabled,
    test: provider?.test,
    acquirer: provider?.acquirer!,
    paymentMethod: provider?.paymentMethod || PaymentMethods.CARD,
    nativeHostedPayment: provider?.nativeHostedPayment,
    rules: {
      ...provider?.rules,
      minAmount: fromCents(provider?.rules?.minAmount),
      maxAmount: fromCents(provider?.rules?.maxAmount)
    },
    metadata: metaToObject(provider?.metadata)
  };

  const goBack = () => history.push(`/accounts/${accountId}?tab=payment-methods`);

  const handleSubmit = async ({
    accountId,
    processor,
    enabled,
    test,
    paymentMethod,
    acquirer,
    nativeHostedPayment,
    rules,
    metadata
  }: Omit<FormValues, 'metadata'> & {metadata: MetadataItemInput[]}) => {
    const update = {
      metadata,
      nativeHostedPayment,
      enabled,
      test,
      acquirer,
      rules: nullify({
        ...rules,
        minAmount: toCents(rules?.minAmount),
        maxAmount: toCents(rules?.maxAmount)
      })
    };
    if (isNew) {
      await createProvider({
        variables: {
          input: {
            accountId,
            processor,
            paymentMethod,
            ...update
          }
        }
      });

      goBack();
    } else {
      Modal.confirm({
        title: 'Are you sure?',
        icon: <ExclamationCircleOutlined />,
        okText: 'Update',
        content:
          'Do you want to update payment method details? This can potentially cause transaction failures.',
        async onOk() {
          await updateProvider({
            variables: {
              input: {
                id: provider!.id,
                ...update
              }
            }
          });
          goBack();
        }
      });
    }
  };

  return (
    <Row>
      <Col span={14} offset={5}>
        <PageHeader
          loading={loadingAccount}
          backPath={`/accounts/${accountId}?tab=payment-methods`}
          title={
            <Stack gutter={10}>
              <Ellipsize>{title}</Ellipsize>
              <ProviderStatusTags provider={provider as ProviderFieldsFragment} />
            </Stack>
          }
        />
        <EditProviderForm
          isNew={isNew}
          accountId={accountId}
          isLoading={loading}
          initialValues={initialValues}
          onSubmit={handleSubmit}
        />
      </Col>
    </Row>
  );
};
